import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle, ExternalLink, FlaskConical, Play, Plus, Trash2, Loader2, XCircle, MoveRight, ChevronDown, Globe, CalendarClock, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { StatusBadge } from "@/components/StatusBadge";
import { MetricCard, MetricGrid } from "@/components/MetricCard";
import { useConfirm } from "@/components/ConfirmDialog";
import { NewTestDialog } from "@/components/NewTestDialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/Pagination";
import { ScheduleDialog } from "@/components/ScheduleDialog";
import { NextRunsList } from "@/components/CountdownCell";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { useProject } from "./ProjectLayout";
import { RefBadge } from "@/components/RefBadge";
import { StabilityChip } from "@/components/StabilityChip";

function MoveTestDialog({ open, onOpenChange, test, suites, onMoved }) {
  const [targetSuiteId, setTargetSuiteId] = useState(test?.suite_id || "");
  const handleMove = async () => {
    if (!targetSuiteId) return;
    try {
      await api.put(`/tests/${test.id}/move`, { suite_id: targetSuiteId });
      toast.success("Test moved");
      onMoved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] rounded-xl border-[#E5E7EB]" data-testid="move-test-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">Move test to a suite</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#475569]">Destination suite</label>
          <Select value={targetSuiteId} onValueChange={setTargetSuiteId}>
            <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="move-test-suite-select">
              <SelectValue placeholder="Select a suite" />
            </SelectTrigger>
            <SelectContent>
              {suites.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`move-target-${s.id}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button onClick={handleMove} disabled={!targetSuiteId || targetSuiteId === test?.suite_id} data-testid="move-test-submit" className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60">
            Move
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveEnvironmentDialog({ open, onOpenChange, test, environments, onMoved }) {
  const [targetEnvId, setTargetEnvId] = useState(test?.default_environment_id || "");
  useEffect(() => {
    setTargetEnvId(test?.default_environment_id || "");
  }, [test]);
  const handleMove = async () => {
    if (!targetEnvId) return;
    try {
      await api.put(`/tests/${test.id}`, { default_environment_id: targetEnvId });
      toast.success("Default environment updated");
      onMoved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] rounded-xl border-[#E5E7EB]" data-testid="move-env-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">Change default environment</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#475569]">Destination environment</label>
          <Select value={targetEnvId} onValueChange={setTargetEnvId}>
            <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="move-env-select">
              <SelectValue placeholder="Select an environment" />
            </SelectTrigger>
            <SelectContent>
              {environments.map((e) => (
                <SelectItem key={e.id} value={e.id} data-testid={`move-env-target-${e.id}`}>
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.type_color || "#64748B" }} />
                    <span>{e.name}</span>
                    <span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{e.type}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-[#94A3B8]">Future runs of this test will target this environment by default.</p>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button onClick={handleMove} disabled={!targetEnvId || targetEnvId === test?.default_environment_id} data-testid="move-env-submit" className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60">
            Update
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectTestsPage() {
  const { projectId } = useParams();
  const { project } = useProject();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [suites, setSuites] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [suiteFilter, setSuiteFilter] = useState("all");
  const [tests, setTests] = useState(null);
  const [total, setTotal] = useState(0);
  const [passing, setPassing] = useState(0);
  const [failing, setFailing] = useState(0);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;
  const [runningId, setRunningId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveEnvOpen, setMoveEnvOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [movingTest, setMovingTest] = useState(null);
  const [schedulesByEntity, setSchedulesByEntity] = useState({});

  const loadActiveSchedules = useCallback(() => {
    api.get(`/schedules?status=active&project_id=${projectId}`)
      .then((r) => {
        const grouped = {};
        for (const s of r.data.items || []) {
          if (!grouped[s.entity_id]) grouped[s.entity_id] = [];
          grouped[s.entity_id].push(s);
        }
        setSchedulesByEntity(grouped);
      })
      .catch(() => {});
  }, [projectId]);

  const loadSuites = useCallback(() => {
    api.get(`/projects/${projectId}/suites`).then((r) => setSuites(r.data.items || [])).catch(() => {});
  }, [projectId]);

  const loadEnvironments = useCallback(() => {
    api.get(`/projects/${projectId}/environments`).then((r) => setEnvironments(r.data.items || [])).catch(() => {});
  }, [projectId]);

  // Reset to page 1 when the suite filter changes.
  useEffect(() => { setPage(1); }, [suiteFilter]);

  const loadTests = useCallback(() => {
    const params = new URLSearchParams({
      project_id: projectId,
      page: String(page),
      per_page: String(PER_PAGE),
    });
    if (suiteFilter !== "all") params.set("suite_id", suiteFilter);
    api.get(`/tests?${params.toString()}`)
      .then((r) => {
        setTests(r.data.items || []);
        setTotal(r.data.total || 0);
        setPassing(r.data.passing || 0);
        setFailing(r.data.failing || 0);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [projectId, suiteFilter, page]);

  useEffect(loadSuites, [loadSuites]);
  useEffect(loadEnvironments, [loadEnvironments]);
  useEffect(loadTests, [loadTests]);
  useEffect(loadActiveSchedules, [loadActiveSchedules]);

  const handleRun = async (t, environmentId = null) => {
    setRunningId(t.id);
    try {
      const qs = environmentId ? `?environment_id=${environmentId}` : "";
      const { data: run } = await api.post(`/tests/${t.id}/run${qs}`);
      navigate(`/runs/${run.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setRunningId(null);
    }
  };

  const handleDelete = async (t) => {
    const ok = await confirm({
      title: "Move this test to the bin?",
      description: `"${t.name}" will be moved to the workspace recycle bin. You can restore it within 30 days.`,
      confirmLabel: "Move to bin",
      danger: true,
      testId: "confirm-delete-test",
    });
    if (!ok) return;
    try {
      await api.delete(`/tests/${t.id}`);
      toast.success("Test moved to bin");
      loadTests();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const suiteName = (id) => suites.find((s) => s.id === id)?.name || "—";
  const envById = (id) => environments.find((e) => e.id === id);

  const columns = [
    {
      key: "name",
      label: "Test name",
      mobileVariant: "title",
      render: (t) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <RefBadge value={t.ref} testId={`test-ref-${t.id}`} />
            <StabilityChip steps={t.steps} testId={`test-stability-${t.id}`} compact />
          </div>
          <Link to={`/tests/${t.id}`} data-testid={`test-name-link-${t.id}`} className="rt-primary hover:text-[#3B82F6] transition-colors mt-1">
            {t.name}
          </Link>
          <span className="text-xs text-[#94A3B8] mt-0.5">{suiteName(t.suite_id)}</span>
        </div>
      ),
    },
    {
      key: "environment",
      label: "Environment",
      render: (t) => {
        const env = envById(t.default_environment_id);
        const url = (t.target_url || "").replace(/^https?:\/\//, "");
        return (
          <div className="flex flex-col gap-0.5" data-testid={`test-env-${t.id}`}>
            {env ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-[#334155]">
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: env.type_color || "#64748B" }} />
                {env.name}
              </span>
            ) : (
              <span className="text-xs text-[#94A3B8]">No environment</span>
            )}
            {url && (
              <a
                href={t.target_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`test-target-url-${t.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-mono text-[#64748B] hover:text-[#3B82F6] truncate"
                title={t.target_url}
              >
                {url.slice(0, 40)}
                <ExternalLink size={10} className="shrink-0" />
              </a>
            )}
          </div>
        );
      },
    },
    { key: "steps", label: "Steps", render: (t) => <span className="text-[#334155]">{t.steps.length}</span> },
    {
      key: "last_run",
      label: "Last run",
      render: (t) => (
        <div className="flex flex-col gap-0">
          {t.last_run ? (
            <Link to={`/runs/${t.last_run.id}`} className="inline-flex flex-col gap-0.5">
              <StatusBadge status={t.last_run.status} testId={`test-status-${t.id}`} />
              <span className="text-[11px] text-[#94A3B8]" data-testid={`test-last-run-at-${t.id}`}>
                {t.last_run.started_at ? formatDateTime(t.last_run.started_at) : ""}
              </span>
            </Link>
          ) : (
            <StatusBadge status={null} testId={`test-status-${t.id}`} />
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (t) => {
        const defaultEnv = envById(t.default_environment_id);
        const upcoming = schedulesByEntity[t.id] || [];
        return (
          <div className="flex flex-col items-end">
            <div className="inline-flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-md shadow-sm">
                  <button
                    onClick={() => handleRun(t)}
                    disabled={runningId === t.id}
                    data-testid={`run-test-button-${t.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 h-8 ${environments.length > 1 ? "rounded-l-md" : "rounded-md"} bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors disabled:opacity-50`}
                  >
                    {runningId === t.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                    {defaultEnv ? `Run on ${defaultEnv.name}` : "Run test"}
                  </button>
                  {environments.length > 1 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`run-env-trigger-${t.id}`}
                          disabled={runningId === t.id}
                          className="inline-flex items-center justify-center w-7 h-8 rounded-r-md bg-[#3B82F6] text-white border-l border-[#2563EB] hover:bg-[#2563EB] transition-colors disabled:opacity-50"
                          aria-label="Pick an environment to run on"
                        >
                          <ChevronDown size={12} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[200px]">
                        <DropdownMenuLabel className="text-[11px] text-[#94A3B8] uppercase tracking-wide">Run this test on…</DropdownMenuLabel>
                        {environments.map((e) => (
                          <DropdownMenuItem
                            key={e.id}
                            onClick={() => handleRun(t, e.id)}
                            data-testid={`run-on-env-${t.id}-${e.id}`}
                          >
                            <span className="inline-block w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: e.type_color || "#64748B" }} />
                            <span className="flex-1">{e.name}</span>
                            <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] ml-2">{e.type}</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`test-row-actions-${t.id}`}
                      aria-label="Row actions"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC]"
                    >
                      <MoreHorizontal size={14} className="text-[#64748B]" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-lg border-[#E5E7EB] p-1 w-52">
                    <DropdownMenuItem
                      onClick={() => { setMovingTest(t); setScheduleOpen(true); }}
                      disabled={environments.length === 0}
                      data-testid={`schedule-test-row-${t.id}`}
                      className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                    >
                      <CalendarClock size={13} className="mr-2 text-[#3B82F6]" />
                      Schedule…
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { setMovingTest(t); setMoveOpen(true); }}
                      data-testid={`move-to-suite-${t.id}`}
                      className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                    >
                      <MoveRight size={13} className="mr-2 text-[#64748B]" />
                      Move to suite…
                    </DropdownMenuItem>
                    {environments.length > 0 && (
                      <DropdownMenuItem
                        onClick={() => { setMovingTest(t); setMoveEnvOpen(true); }}
                        data-testid={`move-to-env-${t.id}`}
                        className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                      >
                        <Globe size={13} className="mr-2 text-[#64748B]" />
                        Change default environment…
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDelete(t)}
                      data-testid={`delete-test-button-${t.id}`}
                      className="rounded-md px-2 py-2 text-sm cursor-pointer text-[#B91C1C] focus:bg-[#FEF2F2]"
                    >
                      <Trash2 size={13} className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {upcoming.length > 0 && (
                <div data-testid={`test-next-wrapper-${t.id}`}>
                  <NextRunsList schedules={upcoming} testIdPrefix={`test-next-${t.id}`} />
                </div>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="project-tests-page">
      <PageHeader
        title="Tests"
        description="All tests across every suite in this project."
        actions={
          <button onClick={() => setNewOpen(true)} data-testid="new-test-button" className="inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors">
            <Plus size={15} />
            Create test
          </button>
        }
      />

      <MetricGrid columns={3}>
        <MetricCard testId="stat-total-tests" label="Total Tests" icon={FlaskConical} value={total} />
        <MetricCard testId="stat-passing" label="Passing" icon={CheckCircle} value={passing} />
        <MetricCard testId="stat-failing" label="Failing" icon={XCircle} value={failing} />
      </MetricGrid>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs font-medium text-[#64748B]">Filter by suite</label>
        <Select value={suiteFilter} onValueChange={setSuiteFilter}>
          <SelectTrigger className="h-9 w-56 rounded-md border-[#E5E7EB] text-sm" data-testid="suite-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All suites</SelectItem>
            {suites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {tests === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : tests.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-tests-message">
          <FlaskConical size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No tests yet</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">Create your first test in this project.</p>
          <button onClick={() => setNewOpen(true)} className="mt-6 inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB]">
            <Plus size={15} />
            Create test
          </button>
        </div>
      ) : (
        <>
          <ResponsiveTable
            testId="tests-table"
            columns={columns}
            rows={tests}
            getRowTestId={(t) => `test-row-${t.id}`}
          />
          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={total}
            onChange={setPage}
            testId="tests-pagination"
          />
        </>
      )}

      <NewTestDialog
        key={`new-test-${suiteFilter}-${suites.length}-${environments.length}`}
        open={newOpen}
        onOpenChange={setNewOpen}
        projectId={projectId}
        suites={suites}
        environments={environments}
        defaultSuiteId={suiteFilter !== "all" ? suiteFilter : suites[0]?.id}
        defaultEnvironmentId={environments[0]?.id}
        defaultTargetUrl={project?.default_target_url || ""}
      />

      <MoveTestDialog
        key={`move-${movingTest?.id || "none"}-${moveOpen}`}
        open={moveOpen}
        onOpenChange={setMoveOpen}
        test={movingTest}
        suites={suites}
        onMoved={loadTests}
      />

      <MoveEnvironmentDialog
        key={`move-env-${movingTest?.id || "none"}-${moveEnvOpen}`}
        open={moveEnvOpen}
        onOpenChange={setMoveEnvOpen}
        test={movingTest}
        environments={environments}
        onMoved={loadTests}
      />

      <ScheduleDialog
        key={`schedule-${movingTest?.id || "none"}-${scheduleOpen}`}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        entityType="test"
        entityId={movingTest?.id}
        entityName={movingTest?.name}
        environments={environments}
        defaultEnvironmentId={movingTest?.default_environment_id || environments[0]?.id}
      />
    </div>
  );
}
